import { ComponentPropsWithoutRef, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { cn } from '@zep/utils';
import { usePriceDirection, usePricingLocale } from '../hooks';
const Root = (props: ComponentPropsWithoutRef<'div'>) => {
  const {
    className,
    children,
    ...rest
  } = props;
  return <div className={cn('w-full border border-[#27262E14] rounded-[12px] h-full lg:min-h-[441px] bg-white shadow-[0_1px_4px_0_rgba(42,39,65,0.08)]', className)} {...rest} data-sentry-component="Root" data-sentry-source-file="PricingCard.tsx">
      {children}
    </div>;
};
const Header = (props: ComponentPropsWithoutRef<'div'>) => {
  const {
    className,
    children,
    ...rest
  } = props;
  return <div className={cn('w-full px-[26px] pb-[14px] pt-[32px]', className)} {...rest} data-sentry-component="Header" data-sentry-source-file="PricingCard.tsx">
      {children}
    </div>;
};
const Content = (props: ComponentPropsWithoutRef<'div'>) => {
  const {
    className,
    children,
    ...rest
  } = props;
  return <div className={cn('px-[26px] pb-[16px] w-full', className)} {...rest} data-sentry-component="Content" data-sentry-source-file="PricingCard.tsx">
      {children}
    </div>;
};
const Footer = (props: ComponentPropsWithoutRef<'div'>) => {
  const {
    className,
    children,
    ...rest
  } = props;
  return <div className={cn('w-full border-t border-gray-[#27262E14] px-[26px] lg:pb-[32px] pb-[18px] pt-[18px]', className)} {...rest} data-sentry-component="Footer" data-sentry-source-file="PricingCard.tsx">
      {children}
    </div>;
};
const Title = (props: ComponentPropsWithoutRef<'div'>) => {
  const {
    className,
    children,
    ...rest
  } = props;
  return <div className={cn('text-gray-900 text-[20px] font-[700]', className)} {...rest} data-sentry-component="Title" data-sentry-source-file="PricingCard.tsx">
      {children}
    </div>;
};
const TitleDescription = (props: ComponentPropsWithoutRef<'div'>) => {
  const {
    className,
    children,
    ...rest
  } = props;
  return <div className={cn(
  // 로케일별 설명 줄 수 차이(1~2줄)에도 카드 정렬이 유지되도록 2줄 높이 확보
  'text-body-2-sb text-[13px] text-gray-600 font-[600] lg:min-h-[37px]', className)} {...rest} data-sentry-component="TitleDescription" data-sentry-source-file="PricingCard.tsx">
      {children}
    </div>;
};
const Logo = (props: ComponentPropsWithoutRef<'div'>) => {
  const {
    className,
    children,
    ...rest
  } = props;
  return <div className={cn('ml-auto', className)} {...rest} data-sentry-component="Logo" data-sentry-source-file="PricingCard.tsx">
      {children}
    </div>;
};
const Price = (props: ComponentPropsWithoutRef<'div'> & {
  pricePerUser: number;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    className,
    pricePerUser,
    ...rest
  } = props;
  const direction = usePriceDirection(pricePerUser);
  const locale = usePricingLocale();
  const priceSuffix = t('subscription:pricing.price.price');
  const priceVariants = useMemo(() => ({
    hidden: {
      opacity: 0.1,
      y: direction === 'down' ? -10 : 10
    },
    // Only vertical movement
    visible: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0.1,
      y: direction === 'down' ? 10 : -10
    }
  }), [direction]);
  return <div className={cn('flex items-end text-gray-900 h-[42px] text-[32px] font-[700]', className)} {...rest} data-sentry-component="Price" data-sentry-source-file="PricingCard.tsx">
      <AnimatePresence mode="wait" data-sentry-element="AnimatePresence" data-sentry-source-file="PricingCard.tsx">
        <motion.div className="flex items-center gap-[2px]" key={String(pricePerUser)} // Change key when price changes to trigger animation
      initial="hidden" animate="visible" exit="hidden" variants={priceVariants} transition={{
        duration: 0.08,
        // Slightly faster but smooth
        ease: 'easeInOut' // Smoother easing for entry/exit
      }} data-sentry-element="unknown" data-sentry-source-file="PricingCard.tsx">
          <span className="text-[28px] font-[600] text-gray-900">
            {locale === 'ko' ? '₩' : '$'}
          </span>
          {pricePerUser.toLocaleString()}
        </motion.div>
      </AnimatePresence>
      <span className="text-body-2-m mb-[9px] ml-[2px] whitespace-nowrap text-[13px] font-[500] text-[#27262ECC]">
        {priceSuffix}
      </span>
    </div>;
};
const FeatureTitle = (props: ComponentPropsWithoutRef<'span'>) => {
  const {
    className,
    children,
    ...rest
  } = props;
  return <span className={cn('text-body-1-sb text-[14px] text-gray-900 font-[600]', className)} {...rest} data-sentry-component="FeatureTitle" data-sentry-source-file="PricingCard.tsx">
      {children}
    </span>;
};
export const PricingCard = {
  Root,
  Title,
  Header,
  Content,
  Footer,
  TitleDescription,
  Logo,
  Price,
  FeatureTitle
};